import { DateTime } from 'luxon'

export type AnyDate = string | Date | DateTime

export function parseDate(isoDate: AnyDate): DateTime {
    if (isoDate instanceof DateTime) {
        return isoDate.startOf('day')
    } else if (isoDate instanceof Date) {
        return DateTime.fromJSDate(isoDate).startOf('day')
    } else if (DateTime.fromSQL(isoDate).isValid) {
        return DateTime.fromSQL(isoDate).startOf('day')
    } else if (DateTime.fromISO(isoDate).isValid) {
        return DateTime.fromISO(isoDate).startOf('day')
    }

    return undefined
}

export function parseDateTime(isoDate: AnyDate): DateTime {
    if (isoDate instanceof DateTime) {
        return isoDate.setLocale('nl')
    } else if (isoDate instanceof Date) {
        return DateTime.fromJSDate(isoDate).setLocale('nl')
    } else if (DateTime.fromSQL(isoDate, { zone: 'Europe/Amsterdam' }).isValid) {
        return DateTime.fromSQL(isoDate, { zone: 'Europe/Amsterdam' }).setLocale('nl')
    } else if (DateTime.fromISO(isoDate, { zone: 'Europe/Amsterdam' }).isValid) {
        return DateTime.fromISO(isoDate, { zone: 'Europe/Amsterdam' }).setLocale('nl')
    }

    return undefined
}

export function compareDate(a: AnyDate, b: AnyDate): number {
    return parseDateTime(a || new Date()).toMillis() - parseDateTime(b || new Date()).toMillis()
}

export function formatIsoDate(isoDate: AnyDate): string {
    return parseDateTime(isoDate)?.toISODate() || ''
}

export function formatIsoDateTime(isoDate: AnyDate): string {
    return parseDateTime(isoDate)?.toISO() || ''
}

export function formatShortDate(isoDate: AnyDate): string {
    return parseDateTime(isoDate)?.toFormat('d LLL') || ''
}

export function formatShortNumericDate(isoDate: AnyDate): string {
    return parseDateTime(isoDate)?.toFormat('dd-LL') || ''
}

export function formatFullDate(isoDate: AnyDate): string {
    return parseDateTime(isoDate)?.toFormat('DD') || ''
}

export function formatFullNumericDate(isoDate: AnyDate): string {
    return parseDateTime(isoDate)?.toFormat('dd-LL-yyyy') || ''
}

export function formatShortDateTime(isoDate: AnyDate): string {
    return parseDateTime(isoDate)?.toFormat('d LLL T') || ''
}

export function formatFullDateTime(isoDate: AnyDate): string {
    return parseDateTime(isoDate)?.toFormat('DD T') || ''
}

export enum TimeFormats {
    Today = 'T',
    CurrentWeek = 'ccc T',
    LastWeek = 'ccc dd-LL',
    Before = 'dd-LL-yyyy',
    Full = 'DDDD T',
}

export const formatDateTime = (date: AnyDate): string => parseDateTime(date)?.toFormat(getTimeFormat(date)) || ''

function getTimeFormat(date: AnyDate) {
    const currentDate = DateTime.now()
    const parsedDate = parseDateTime(date) || undefined

    if (inToday(currentDate, parsedDate)) {
        return TimeFormats.Today
    } else if (inCurrentWeek(currentDate, parsedDate)) {
        return TimeFormats.CurrentWeek
    } else if (inLastWeek(currentDate, parsedDate)) {
        return TimeFormats.LastWeek
    } else {
        return TimeFormats.Before
    }
}

function inLastWeek(currentDate: DateTime, parsedDate: DateTime) {
    // If current week is the first week of a year, check for year difference and week number of the parsed date
    if (currentDate.weekNumber === 1) {
        return currentDate.year - parsedDate.year === 1 && parsedDate.weekNumber === parsedDate.weeksInWeekYear
    }
    return currentDate.weekNumber - parsedDate.weekNumber === 1 && currentDate.year === parsedDate.year
}

function inCurrentWeek(currentDate: DateTime, parsedDate: DateTime) {
    return currentDate.weekNumber === parsedDate.weekNumber && currentDate.year === parsedDate.year
}

function inToday(currentDate: DateTime, parsedDate: DateTime) {
    return currentDate.day === parsedDate.day && currentDate.diff(parsedDate).as('days') < 1
}

export function formatDifferenceInTime(start: AnyDate, end: AnyDate, minimalValue: string = '00:00'): string {
    const startDate = parseDateTime(start)
    const endDate = parseDateTime(end)
    const diff = endDate.diff(startDate)
    return diff.toMillis() < 60000 ? minimalValue : diff.toFormat('h:mm')
}

export function applyLocalTimeZone(date: AnyDate): DateTime {
    return parseDateTime(date)?.setZone(DateTime.now().zone)
}
